import React, { useCallback, useEffect, useState } from 'react';
import { getWorkTypesAdmin, getWorkTypeUnits, getEmployees, updateWorkType, deleteWorkType } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { WorkTypeDialog } from '../../components/rates/WorkTypeDialog';
import { RateHistoryDialog } from '../../components/rates/RateHistoryDialog';
import { EmployeeRatesTab } from '../../components/rates/EmployeeRatesTab';
import { PricesTab } from '../../components/rates/PricesTab';
import { formatDate, formatEur } from '../../lib/workStatus';
import { Calculator, Plus, Pencil, History, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const WorkTypesManagement = () => {
  const [workTypes, setWorkTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [dialog, setDialog] = useState({ open: false, workType: null });
  const [history, setHistory] = useState({ open: false, workType: null });

  const load = useCallback(async () => {
    try {
      const [types, unitList, employeeList] = await Promise.all([getWorkTypesAdmin(), getWorkTypeUnits(), getEmployees()]);
      setWorkTypes(types || []);
      setUnits(unitList || []);
      setEmployees(employeeList || []);
    } catch (error) {
      toast.error('Neizdevās ielādēt darba veidus');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const replaceType = (updated) => {
    setWorkTypes((prev) => prev.map((w) => (w.key === updated.key ? updated : w)));
    setHistory((prev) => (prev.workType?.key === updated.key ? { ...prev, workType: updated } : prev));
  };

  const toggleActive = async (wt) => {
    try {
      replaceType(await updateWorkType(wt.key, { active: !wt.active }));
      toast.success(wt.active ? 'Darba veids deaktivizēts' : 'Darba veids aktivizēts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās saglabāt');
    }
  };

  const remove = async (wt) => {
    const used = wt.usage.tasks + wt.usage.entries > 0;
    if (!window.confirm(used ? `"${wt.label}" tiek izmantots ${wt.usage.tasks} uzdevumos un ${wt.usage.entries} ierakstos — tas tiks deaktivizēts, vēsture saglabāsies. Turpināt?` : `Dzēst darba veidu "${wt.label}"?`)) return;
    try {
      const result = await deleteWorkType(wt.key);
      toast.success(result.message);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Neizdevās dzēst');
    }
  };

  const visible = workTypes.filter((w) => showInactive || w.active);
  const now = new Date();

  return (
    <div className="space-y-6" data-testid="admin-work-types-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F3D2B] flex items-center gap-2" data-testid="admin-work-types-title"><Calculator className="w-6 h-6" /> Darba veidi un likmes</h1>
          <p className="text-[#2E2E2E]/70">Darba veidu katalogs, standarta likmes ar vēsturi, individuālās darbinieku likmes un uzņēmuma cenas. Alga = apjoms × likme, kas bija spēkā darba dienā.</p>
        </div>
        <Button onClick={() => setDialog({ open: true, workType: null })} className="bg-[#1F3D2B] hover:bg-[#2a5039]" data-testid="admin-work-types-add"><Plus className="w-4 h-4 mr-2" /> Jauns darba veids</Button>
      </div>

      <Tabs defaultValue="types" data-testid="admin-work-types-tabs">
        <TabsList>
          <TabsTrigger value="types" data-testid="admin-work-types-tab-types">Darba veidi</TabsTrigger>
          <TabsTrigger value="employee" data-testid="admin-work-types-tab-employee">Individuālās likmes</TabsTrigger>
          <TabsTrigger value="prices" data-testid="admin-work-types-tab-prices">Uzņēmuma cenas</TabsTrigger>
        </TabsList>

        <TabsContent value="types" className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-[#2E2E2E]/70 cursor-pointer">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="admin-work-types-show-inactive" /> Rādīt deaktivizētos
          </label>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-40" data-testid="admin-work-types-loading"><div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-work-types-table">
                    <thead className="bg-[#f8faf8] text-left">
                      <tr><th className="px-4 py-3">Darba veids</th><th className="px-4 py-3">Mērvienība</th><th className="px-4 py-3">Standarta likme</th><th className="px-4 py-3">Spēkā no</th><th className="px-4 py-3">Izmantots</th><th className="px-4 py-3">Aktīvs</th><th className="px-4 py-3 text-right">Darbības</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visible.map((wt) => {
                        const current = wt.rates.find((r) => new Date(r.valid_from) <= now);
                        const future = wt.rates.filter((r) => new Date(r.valid_from) > now).length;
                        return (
                          <tr key={wt.key} className={wt.active ? '' : 'opacity-60'} data-testid={`admin-work-type-row-${wt.key}`}>
                            <td className="px-4 py-3 font-medium">{wt.label}<div className="text-xs text-[#2E2E2E]/40 font-mono">{wt.key}</div></td>
                            <td className="px-4 py-3">{wt.unit}</td>
                            <td className="px-4 py-3 whitespace-nowrap font-semibold" data-testid={`admin-work-type-rate-${wt.key}`}>{formatEur(wt.rate)} / {wt.unit}{future > 0 && <Badge className="ml-2 bg-blue-100 text-blue-700">+{future} nākotnē</Badge>}</td>
                            <td className="px-4 py-3 text-[#2E2E2E]/70">{current ? formatDate(current.valid_from) : '-'}</td>
                            <td className="px-4 py-3 text-[#2E2E2E]/70">{wt.usage.tasks} uzd. · {wt.usage.entries} ier.</td>
                            <td className="px-4 py-3"><Switch checked={wt.active} onCheckedChange={() => toggleActive(wt)} data-testid={`admin-work-type-active-${wt.key}`} /></td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <Button size="sm" variant="outline" onClick={() => setHistory({ open: true, workType: wt })} data-testid={`admin-work-type-history-${wt.key}`}><History className="w-4 h-4 mr-1" /> Likmes</Button>
                              <Button size="sm" variant="ghost" onClick={() => setDialog({ open: true, workType: wt })} data-testid={`admin-work-type-edit-${wt.key}`}><Pencil className="w-4 h-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => remove(wt)} data-testid={`admin-work-type-delete-${wt.key}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                            </td>
                          </tr>
                        );
                      })}
                      {visible.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-[#2E2E2E]/60" data-testid="admin-work-types-empty">Nav darba veidu.</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employee">
          <Card className="border-0 shadow-sm"><CardContent className="p-4 sm:p-6"><EmployeeRatesTab employees={employees} workTypes={workTypes} /></CardContent></Card>
        </TabsContent>

        <TabsContent value="prices">
          <PricesTab />
        </TabsContent>
      </Tabs>

      <WorkTypeDialog open={dialog.open} onOpenChange={(open) => setDialog((p) => ({ ...p, open }))} workType={dialog.workType} units={units} onSaved={load} />
      <RateHistoryDialog open={history.open} onOpenChange={(open) => setHistory((p) => ({ ...p, open }))} workType={history.workType} onChanged={replaceType} />
    </div>
  );
};

export default WorkTypesManagement;
